import {
   API_ENDPOINTS,
   BottomAnchoredControls,
   Button,
   ButtonTypes,
   Card,
   CardBody,
   customToast,
   Icon,
   LinkifyText,
   Modal,
   PageLayoutPublic,
   route,
   Stepper,
   StringHelpers,
   useDisabledContext,
   useForm,
   useLangContext,
   useNavigationGuard,
   useScrollToError,
   useStepper,
   Variants,
   WidthConstrainedContainer,
} from 'carrier-fe';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { STEPS, TOTAL_STEPS } from '../../Constants/InstallationJourney';
import { RegistrationJourney } from '../../types/RegistrationJourney';
import { configurePayload } from './helpers';
import axios, { AxiosResponse } from 'axios';
import { HouseholderInstallationView } from '../../types/Registration/Journey';
import Step2 from './steps/Step2';
import Step1 from './steps/Step1';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import Step5 from './steps/Step5';

const loadPageLang = async (
   pageLang: any,
   getLangPage: Function,
   setPageLang: Dispatch<SetStateAction<any>>
) => {
   if (pageLang) return;
   try {
      const response = await getLangPage('registration/householder/registration');
      setPageLang(response);
   } catch (error: any) {
      console.error(error);
   }
};

const defaultValues: RegistrationJourney = {
   // STEP 1 (now registered_by)
   registered_by: '',
   // STEP 2 (now system details)
   system_type: '',
   completion_date: '',
   serial_number: '',
   address_line_1: '',
   address_line_2: '',
   address_line_3: '',
   town_city: '',
   postcode_zipcode: '',
   local_authority_code: '',
   sub_country_name: '',
   country_code_iso_3: '',
   state_county: '',
   // STEP 3 ...
   contact_type: '',
   first_name: '',
   last_name: '',
   email: '',
   landline_phone_prefix: '',
   landline_phone_number: '',
   mobile_phone_prefix: '',
   mobile_phone_number: '',
   // STEP 4 ...
   company_address_id: '',
   company_name: '',
   company_address_line_1: '',
   company_address_line_2: '',
   company_address_line_3: '',
   company_town_city: '',
   company_postcode_zipcode: '',
   company_local_authority_code: '',
   company_sub_country_name: '',
   company_country_code_iso_3: '',
   company_state_county: '',
   company_gas_safe_number: '',
   // STEP 5
   confirmed: false,
};

function Register() {
   const params = useParams();
   const [searchParams] = useSearchParams();
   const [pageLang, setPageLang] = useState<any>();
   const [isLoading, setLoading] = useState(false);
   const [helpModalOpen, setHelpModalOpen] = useState(false);
   const installationId = params?.registrationId || searchParams.get('installationId');
   const isEdit = Boolean(installationId);

   // hooks
   const navigate = useNavigate();
   const { disabled, setDisabled } = useDisabledContext();

   const { crud, getLangPage } = useLangContext();
   const { activeStep, isFirstStep, isLastStep, nextStep, prevStep } = useStepper({
      initialStep: STEPS.STEP1,
      totalSteps: TOTAL_STEPS,
   });

   // form initialization
   const submit = async () => {
      const payload = configurePayload(form?.store, activeStep);
      const installationIdFromSearchParams = searchParams.get('installationId');
      const installationIdToUse = installationId || installationIdFromSearchParams;

      let response:
         | AxiosResponse<{ data: HouseholderInstallationView; message?: string }>
         | undefined;

      if (isFirstStep && !isEdit) {
         response = await axios.post(route(API_ENDPOINTS.HOUSEHOLDER.REGISTRATION.STORE), payload);
      } else if (installationIdToUse && activeStep) {
         response = await axios.patch(
            route(API_ENDPOINTS.HOUSEHOLDER.REGISTRATION.UPDATE, {
               installationId: installationIdToUse,
               step: String(activeStep),
            }),
            payload
         );
      } else {
         customToast({
            variant: Variants.Danger,
            title: 'Something went wrong',
         });
         throw new Error('Invalid step or installationId');
      }

      // Item created. happens at step 1 only
      if (response?.status === 201) {
         const { data } = response;
         customToast({
            title: data?.message || 'Success',
            variant: Variants.Success,
         });
         nextStep({
            installationId: data?.data?.id,
         });
         // every other call
      } else if (response?.status === 200) {
         const { data } = response;
         if (isLastStep) {
            setDisabled(false);
            navigate(`/installation/${data?.data?.id}`);
         } else {
            customToast({
               title: data?.message || 'Success',
               variant: Variants.Success,
            });
            nextStep();
         }
      }
   };
   const form = useForm(submit as any, defaultValues, { silentSuccess: true });
   const { Submit, errors, isSuccess, data } = form;
   useNavigationGuard({
      message: crud?.modals?.alert?.description?.at(0) || '',
      isEnabled: !isLastStep,
   });
   const formRef = useScrollToError(form?.errors);

   // Effects
   useEffect(() => {
      if (isEdit) {
         fetchActiveStepData();
         // fetchInstallationRegistration();
      }
      loadPageLang(pageLang, getLangPage, setPageLang);
   }, [isEdit, activeStep]);

   // misc
   const handleBack = () => {
      prevStep();
   };

   // fetch active step data
   const fetchActiveStepData = async () => {
      try {
         setLoading(true);
         setDisabled(true);
         if (installationId) {
            const response = await axios.get<{ data: HouseholderInstallationView }>(
               route(API_ENDPOINTS.HOUSEHOLDER.REGISTRATION.EDIT, {
                  installationId,
                  step: String(activeStep),
               })
            );
            const { data } = response;
            if (data) {
               form?.setValues({
                  ...(data?.data as any),
               });
               form?.setErrors({});
            }
            setDisabled(false);
         }
      } catch (error: any) {
         customToast({
            variant: Variants.Danger,
            title: error?.response?.data?.message || 'Something went wrong',
         });
      } finally {
         setLoading(false);
         setDisabled(false);
      }
   };

   const btnLabel = useMemo(() => {
      const stepType = isLastStep ? 'confirm' : 'next';
      const defaultLabel = crud?.buttons?.[stepType]?.default || (isLastStep ? 'Confirm' : 'Next');
      const loadingLabel =
         crud?.buttons?.[stepType]?.submitting || (isLastStep ? 'Confirming...' : 'Saving...');

      return { loadingLabel, defaultLabel };
   }, [isLastStep, crud]);

   return (
      <PageLayoutPublic
         progressBar={{ activeStep, totalSteps: TOTAL_STEPS }}
         title="Register Installation"
      >
         <WidthConstrainedContainer maxWidth={1440}>
            <Card style={{ maxWidth: '1440px', minHeight: 750 }}>
               <CardBody>
                  <div ref={formRef}>
                     <Stepper
                        title={StringHelpers.upper(
                           `${crud?.sub_titles?.step || 'step'} ${activeStep}/${TOTAL_STEPS}`
                        )}
                        activeStep={activeStep}
                        rightSlot={
                           <>
                              <div
                                 onClick={() => setHelpModalOpen(true)}
                                 role="button"
                                 className="pe d-flex align-items-center gap-1 text-primary"
                              >
                                 <span className="fw-semibold" style={{ fontSize: '17px' }}>
                                    {pageLang?.page?.help_modal?.title || 'Help'}
                                 </span>
                                 <Icon icon={'help'} />
                              </div>
                              <Modal
                                 open={helpModalOpen}
                                 onClose={() => setHelpModalOpen(false)}
                                 title={pageLang?.page?.help_modal?.title || 'Help'}
                              >
                                 <LinkifyText text={pageLang?.page?.help_modal?.description || "If you need help locating your Viessmann product's serial number, visit https://www.viessmann.co.uk/en/support/spare-parts-app/serial-number-locator-for-viessmann-products.html for model-specific instructions and images."} />
                              </Modal>
                           </>
                        }
                     >
                        {[Step1, Step2, Step3, Step4, Step5].map((Step, index) => (
                           <Step loading={isLoading} form={form} pageLang={pageLang} />
                        ))}
                     </Stepper>
                  </div>
               </CardBody>
            </Card>
         </WidthConstrainedContainer>

         <BottomAnchoredControls
            style={{
               position: 'fixed',
               bottom: 0,
               right: 0,
               padding: '1.5rem 2.5rem',
            }}
         >
            <div className="me-auto ms-5 w-full">
               <Button
                  label={StringHelpers.title(crud?.buttons?.exit?.default || 'Exit')}
                  onClick={() => {
                     window.confirm(crud?.modals?.alert?.description?.at(0)) && navigate('/');
                  }}
                  variant={Variants.Dark}
                  type={ButtonTypes.Outline}
                  disabled={disabled}
               />
            </div>
            {!isFirstStep && (
               <Button
                  label={crud?.buttons?.previous?.default || 'Previous'}
                  onClick={handleBack}
                  variant={Variants.Primary}
                  type={ButtonTypes.Outline}
                  className={'me-4'}
                  disabled={disabled}
               />
            )}
            <Submit
               disabled={disabled}
               defaultLabel={btnLabel.defaultLabel}
               loadingLabel={btnLabel.loadingLabel}
            />
         </BottomAnchoredControls>
      </PageLayoutPublic>
   );
}

export default Register;
